import i18next from 'i18next';
import { Countries, Languages, LocaleLanguageCode } from '../Constants/Common';
import { Country, Language } from 'components/common/CommonTypes';
import { ValuesType } from 'types/generic';

export const nonUsLocations = Object.values(Countries).filter(
    (location) => location !== Countries.Us
);

// is used to map currently used languages (like 'en', 'us', set in translation api) to locale codes
const languageAliasesMap: Record<
    Language,
    ValuesType<typeof LocaleLanguageCode>
> = {
    [Languages.Us]: LocaleLanguageCode.US,
    [Languages.Uk]: LocaleLanguageCode.GB,
    [Languages.At]: LocaleLanguageCode.AT,
    [Languages.De]: LocaleLanguageCode.DE,
    [Languages.Es]: LocaleLanguageCode.ES
};

export function isAllowedInLocation(
    allowedLocations: Country[],
    currentLocation: Country
) {
    return (
        !allowedLocations ||
        !location ||
        allowedLocations.includes(currentLocation)
    );
}

export const normalizeLanguageCode = (lang?: string) => {
    const alias = lang?.toLowerCase() ?? '';
    return languageAliasesMap[alias] || lang;
};

export const getTranslatedCountryAndLanguage = (localeCode: string) => {
    try {
        const locale = new Intl.Locale(localeCode);

        const country =
            new Intl.DisplayNames([localeCode], {
                type: 'region'
            }).of(locale.region) ?? '';

        const language =
            new Intl.DisplayNames([localeCode], {
                type: 'language'
            }).of(locale.language) ?? localeCode.toUpperCase();

        return { language, country };
    } catch {
        return { language: localeCode.toUpperCase(), country: '' };
    }
};

const mapLocationToLanguage = (location: Country) => {
    const map: Record<Country, Language> = {
        [Countries.Uk]: Languages.Uk,
        [Countries.At]: Languages.De, // Austrian language variant is not supported atm so using de
        [Countries.De]: Languages.De,
        [Countries.Us]: Languages.Us,
        [Countries.Es]: Languages.Es
    };

    return map[location] ?? Languages.Uk;
};

// TODO: remove when language switcher is released
export const setLanguageByCountrySafe = (country) => {
    const lang = mapLocationToLanguage(country);
    const safeLanguage = normalizeLanguageCode(lang);
    i18next.changeLanguage(safeLanguage);
};
